import React, { useEffect, useMemo } from 'react';
import { z } from 'zod';
import { clsx } from 'clsx';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useValidationErrors } from '@/Hooks/useValidationErrors';
import BaseTextInput from './Form/BaseTextInput';
import BaseDropdown from './Form/BaseDropdown';
import { Button } from "primereact/button";
import { Team } from "@/Stub/V1";
import { useGetClients } from "@/Service/Api/V1/ApiHooks/Clients/useGetClients";
import ContentLoadingTemplate from "@/Components/Templates/ContentLoadingTemplate";


const TeamFormSchema = z.object({
    client_id: z.number(),
    name: z.string({
        required_error: 'Name is required'
    }),
});

export type TeamFormData = z.infer<typeof TeamFormSchema>;


export type TeamFormFormProps = {
    className?: string
    onSubmit?: (data: TeamFormData) => void
    onSubmitButtonLabel?: string
    onSubmitButtonDisabled?: boolean
    isProcessing?: boolean
    initFormData?: Team
    errorBag?: Error[]
};

const TeamForm: React.FC<TeamFormFormProps> = ({
    className,
    onSubmit,
    isProcessing,
    initFormData,
    onSubmitButtonLabel,
    onSubmitButtonDisabled = false,
    errorBag
}: TeamFormFormProps) => {

    const formInitialValues = useMemo(() => {
        if(initFormData){
            return {
                ...initFormData,
                name: initFormData.name,
                client_id: initFormData?.client?.id,
            };
        }

        return {
            client_id: undefined,
            name: '',
        };
    }, [initFormData]);

    const {
        control,
        setError,
        reset,
        formState: { errors },
        handleSubmit,
    } = useForm<TeamFormData>({
        resolver: zodResolver(TeamFormSchema),
        defaultValues: { ...formInitialValues }
    });

    const onFormSubmit = (data: TeamFormData) => {
        if (onSubmit) {
            onSubmit(data);
        }
    };

    const { data, isLoading: isLoadingClients } = useGetClients({
        requestParams: {
            page: 1,
            per_page: 1000
        }
    });

    const organizationOptions = useMemo(() => {
        const clients = data?.data || [];
        return [
            ...clients.map(client => ({
                key: client.id,
                label: client.name,
                value: client.id
            }))
        ];
    }, [data]);



    useEffect(() => {
        if(initFormData) {
            reset({
                ...initFormData,
                name: initFormData.name,
                client_id: initFormData?.client?.id,
            });
        } else {
            reset({
                client_id: organizationOptions.length > 0 ? organizationOptions[0].value : undefined,
                name: '',
            });
        }
    }, [initFormData, organizationOptions, reset]);

    useValidationErrors(setError, errorBag);

    if(isLoadingClients) {
        return <ContentLoadingTemplate />;
    }

    return (
        <div className={clsx(className, 'user-form')}>
            <form onSubmit={handleSubmit(onFormSubmit)}>
                <div className="grid">
                    <h4 className={'col-12 my-0 font-bold'}>Team info</h4>
                    <BaseTextInput
                        required
                        className={"col-6 rounded-md"}
                        pt={{ inputText: { className: 'w-full' } }}
                        control={control}
                        name={"name"}
                        label={'Name'}
                        errorMessages={errors.name?.message}
                    />
                    <BaseDropdown
                        required
                        className={"col-6 rounded-md"}
                        name={"client_id"}
                        pt={{ dropdown: { className: 'w-full' } }}
                        label="Organization"
                        control={control}
                        errorMessages={errors.client_id?.message}
                        options={organizationOptions}
                    />
                </div>
                <div className="flex mt-4">
                    <Button
                        className={"ml-auto"}
                        label={onSubmitButtonLabel}
                        disabled={onSubmitButtonDisabled}
                        type={"submit"}
                        severity={"success"}
                        loading={isProcessing}
                    />
                </div>
            </form>
        </div>
    );
};

export default TeamForm;
